import { cva } from '@/styled-system/css';

export const iconButtonRecipe = cva({
  base: {
    'alignItems': 'center',
    'cursor': 'pointer',
    'display': 'inline-flex',
    'justifyContent': 'center',
    'position': 'relative',
    "&[aria-pressed='true']:disabled": {
      opacity: 1,
    },
    'boxShadowColor': 'transparent.invisible', // Sets a default `var(--shadow-color)` so it is always defined
    'transitionProperty': {
      _motionReduce: 'none',
      _motionSafe: 'box-shadow, background-color',
    },
    'transitionDuration': {
      _motionReduce: '0s',
      _motionSafe: '{durations.200}',
    },
    'transitionTimingFunction': '{easings.ease}',
  },
  variants: {
    tone: {
      'brand': {},
      'neutral': {},
      'neutral-fixed': {},
      'critical': {},
    },
    emphasis: {
      bare: {
        focusOutline: true,
      },
      filled: {
        boxShadow: {
          base: `inset 0 0 0 {borderWidths.default} var(--shadow-color)`,
          _focusVisible: `inset 0 0 0 {borderWidths.default} var(--shadow-color), {shadows.focus}`,
        },
      },
      outlined: {
        boxShadow: {
          base: `inset 0 0 0 {borderWidths.default} var(--shadow-color)`,
          _focusVisible: `inset 0 0 0 {borderWidths.default} var(--shadow-color), {shadows.focus}`,
        },
      },
    },
    disabled: {
      true: {
        cursor: 'default',
      },
      false: {
        cursor: 'pointer',
      },
    },
    loading: {
      true: {},
      false: {},
    },
    onImpact: {
      true: {},
      false: {},
    },
    shape: {
      circle: {
        borderRadius: 'shape.ellipse',
      },
      square: {
        borderRadius: 'obj.default',
      },
    },
    size: {
      xs: {
        'height': 'action.xs',
        'width': 'action.xs',
        '& > :where(svg)': {
          width: 'icon.sm',
          height: 'icon.sm',
        },
      },
      sm: {
        'height': 'action.sm',
        'width': 'action.sm',
        '& > :where(svg)': {
          width: 'icon.sm',
          height: 'icon.sm',
        },
      },
      md: {
        'height': 'action.md',
        'width': 'action.md',
        '& > :where(svg)': {
          width: 'icon.md',
          height: 'icon.md',
        },
      },
      lg: {
        'height': 'action.lg',
        'width': 'action.lg',
        '& > :where(svg)': {
          width: 'icon.md',
          height: 'icon.md',
        },
      },
    },
  },
  compoundVariants: [
    {
      disabled: true,
      loading: false,
      css: {
        opacity: 'disabled',
      },
    },
    {
      size: ['xs', 'sm'],
      css: {
        _after: {
          content: '""',
          position: 'absolute',
          height: 'tapArea.mobile.default',
          minWidth: 'tapArea.mobile.default',
          width: '100%',
          left: '50%',
          top: '50%',
          marginBlockStart: 'calc(({sizes.tapArea.mobile.default} / 2) * -1)',
          marginInlineStart: 'calc(({sizes.tapArea.mobile.default} / 2) * -1)',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'bg.action.hero.default',
        },
        boxShadowColor: {
          base: 'border.invisible',
        },
        color: {
          base: 'icon.onHero.action.neutral.default',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'bg.action.critical.hero.default',
        },
        boxShadowColor: {
          base: 'border.invisible',
        },
        color: {
          base: 'icon.onHero.action.neutral.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'bg.action.neutral.alt.default',
        },
        boxShadowColor: {
          base: 'border.action.hero.default',
        },
        color: {
          base: 'icon.action.hero.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral',
      css: {
        backgroundColor: {
          base: 'IconButton.outlined.neutral.bg.default',
        },
        boxShadowColor: {
          base: 'border.action.neutral.default',
        },
        color: {
          base: 'icon.neutral',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'bg.action.critical.neutral.default',
        },
        boxShadowColor: {
          base: 'border.action.critical.hero.default',
        },
        color: {
          base: 'icon.action.critical.hero.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      css: {
        backgroundColor: {
          base: 'bg.action.neutral-fixed.default',
        },
        boxShadowColor: {
          base: 'border.action.neutral-fixed.default',
        },
        color: {
          base: 'icon.action.neutral-fixed.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'bg.action.bare.neutral.default',
        },
        color: {
          base: 'icon.action.hero.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral',
      css: {
        backgroundColor: {
          base: 'bg.action.bare.neutral.default',
        },
        color: {
          base: 'icon.neutral',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
        color: {
          base: 'icon.action.critical.hero.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral-fixed',
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
        color: {
          base: 'icon.action.neutral-fixed.default',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'brand',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'bg.action.neutral.alt.default',
        },
        boxShadowColor: {
          base: 'border.invisible',
        },
        color: {
          base: 'icon.action.hero.default',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'critical',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'bg.action.critical.neutral.default',
        },
        boxShadowColor: {
          base: 'IconButton.onImpact.filled.critical.border.default',
        },
        color: {
          base: 'icon.action.critical.hero.default',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'neutral-fixed',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'bg.action.neutral-fixed.default',
        },
        boxShadowColor: {
          base: 'border.invisible',
        },
        color: {
          base: 'icon.action.neutral-fixed.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'brand',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
        boxShadowColor: {
          base: 'border.onImpact.action.hero.default',
        },
        color: {
          base: 'icon.onImpact.action.hero.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'bg.action.impact.bare.default',
        },
        boxShadowColor: {
          base: 'border.onImpact.neutral',
        },
        color: {
          base: 'icon.onImpact.neutral',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'critical',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
        boxShadowColor: {
          base: 'border.onImpact.action.critical.hero.default',
        },
        color: {
          base: 'icon.onImpact.action.critical.hero.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
        boxShadowColor: {
          base: 'border.onImpact.neutral-fixed',
        },
        color: {
          base: 'icon.onImpact.neutral-fixed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
        color: {
          base: 'IconButton.onImpact.bare.neutral.icon.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral-fixed',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
        color: {
          base: 'icon.onImpact.neutral-fixed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'brand',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
        color: {
          base: 'icon.onImpact.action.hero.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'critical',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
        color: {
          base: 'icon.onImpact.action.critical.hero.default',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'brand',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.hero.hover',
          _active: 'bg.action.hero.pressed',
        },
        boxShadowColor: {
          _hover: 'border.invisible',
          _active: 'border.invisible',
        },
        color: {
          _hover: 'icon.onHero.action.neutral.hover',
          _active: 'icon.onHero.action.neutral.pressed',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'critical',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.critical.hero.hover',
          _active: 'bg.action.critical.hero.pressed',
        },
        boxShadowColor: {
          _hover: 'border.invisible',
          _active: 'IconButton.filled.critical.border.pressed',
        },
        color: {
          _hover: 'icon.onHero.action.neutral.hover',
          _active: 'IconButton.filled.critical.icon.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'brand',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.neutral.alt.hover',
          _active: 'bg.action.neutral.alt.pressed',
        },
        boxShadowColor: {
          _hover: 'border.action.hero.hover',
          _active: 'border.action.hero.pressed',
        },
        color: {
          _hover: 'icon.action.hero.hover',
          _active: 'icon.action.hero.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.neutral.hover',
          _active: 'bg.action.neutral.pressed',
        },
        boxShadowColor: {
          _hover: 'border.action.neutral.hover',
          _active: 'border.action.neutral.pressed',
        },
        color: {
          _hover: 'icon.neutral',
          _active: 'icon.neutral',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'critical',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.critical.neutral.hover',
          _active: 'bg.action.critical.neutral.pressed',
        },
        boxShadowColor: {
          _hover: 'border.action.critical.hero.hover',
          _active: 'border.action.critical.hero.pressed',
        },
        color: {
          _hover: 'icon.action.critical.hero.hover',
          _active: 'icon.action.critical.hero.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.neutral-fixed.hover',
          _active: 'bg.action.neutral-fixed.pressed',
        },
        boxShadowColor: {
          _hover: 'border.action.neutral-fixed.hover',
          _active: 'border.action.neutral-fixed.pressed',
        },
        color: {
          _hover: 'icon.action.neutral-fixed.hover',
          _active: 'icon.action.neutral-fixed.pressed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'brand',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.bare.neutral.hover',
          _active: 'bg.action.bare.neutral.pressed',
        },
        color: {
          _hover: 'icon.accent.blue.strong-fixed',
          _active: 'icon.accent.blue.strong-fixed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.bare.neutral.hover',
          _active: 'bg.action.bare.neutral.pressed',
        },
        color: {
          _hover: 'icon.neutral-fixed',
          _active: 'icon.neutral-fixed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'critical',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.critical.neutral.hover',
          _active: 'bg.action.critical.neutral.pressed',
        },
        color: {
          _hover: 'icon.action.critical.hero.hover',
          _active: 'icon.action.critical.hero.pressed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral-fixed',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.neutral-fixed.hover',
          _active: 'bg.action.neutral-fixed.pressed',
        },
        color: {
          _hover: 'icon.action.neutral-fixed.hover',
          _active: 'icon.action.neutral-fixed.pressed',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'brand',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.neutral.alt.hover',
          _active: 'bg.action.neutral.alt.pressed',
        },
        color: {
          _hover: 'icon.action.hero.hover',
          _active: 'icon.action.hero.pressed',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'critical',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.critical.neutral.hover',
          _active: 'bg.action.critical.neutral.pressed',
        },
        color: {
          _hover: 'icon.action.critical.hero.hover',
          _active: 'icon.action.critical.hero.pressed',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'neutral-fixed',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.neutral-fixed.hover',
          _active: 'bg.action.neutral-fixed.pressed',
        },
        boxShadowColor: {
          _hover: 'border.invisible',
          _active: 'border.invisible',
        },
        color: {
          _hover: 'icon.action.neutral-fixed.hover',
          _active: 'icon.action.neutral-fixed.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'brand',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.neutral.alt.hover',
          _active: 'bg.action.neutral.alt.pressed',
        },
        boxShadowColor: {
          _hover: 'border.action.hero.hover',
          _active: 'border.action.hero.pressed',
        },
        color: {
          _hover: 'icon.action.hero.hover',
          _active: 'icon.action.hero.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.impact.bare.hover',
          _active: 'bg.action.impact.bare.pressed',
        },
        boxShadowColor: {
          _hover: 'border.onImpact.neutral',
          _active: 'border.onImpact.neutral',
        },
        color: {
          _hover: 'icon.neutral',
          _active: 'icon.neutral',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'critical',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.critical.neutral.hover',
          _active: 'bg.action.critical.neutral.pressed',
        },
        boxShadowColor: {
          _hover: 'border.action.critical.hero.hover',
          _active: 'border.action.critical.hero.pressed',
        },
        color: {
          _hover: 'icon.action.critical.hero.hover',
          _active: 'icon.action.critical.hero.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.impact.bare.fixed.hover',
          _active: 'bg.action.impact.bare.fixed.pressed',
        },
        boxShadowColor: {
          _hover: 'border.neutral-fixed',
          _active: 'border.neutral-fixed',
        },
        color: {
          _hover: 'icon.neutral-fixed',
          _active: 'icon.neutral-fixed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.impact.bare.hover',
          _active: 'bg.action.impact.bare.pressed',
        },
        color: {
          _hover: 'icon.neutral',
          _active: 'icon.neutral',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral-fixed',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.impact.bare.fixed.hover',
          _active: 'bg.action.impact.bare.fixed.pressed',
        },
        color: {
          _hover: 'icon.neutral-fixed',
          _active: 'icon.neutral-fixed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'brand',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.neutral.alt.hover',
          _active: 'bg.action.neutral.alt.pressed',
        },
        color: {
          _hover: 'icon.action.hero.hover',
          _active: 'icon.action.hero.pressed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'critical',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.critical.neutral.hover',
          _active: 'bg.action.critical.neutral.pressed',
        },
        color: {
          _hover: 'icon.action.critical.hero.hover',
          _active: 'icon.action.critical.hero.pressed',
        },
      },
    },
  ],
});
